import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Typography,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import { Save, Restore, AutoAwesome } from '@mui/icons-material';
import { dhis2ConfigAPI, type Dhis2ConfigOut } from '../../services/api';
import AIPromptsEditor from './AIPromptsEditor';

const DEFAULT_PROMPT = `You are extracting structured data from a clinical conversation.
Schema: {schema_definition}
Synonyms per field: {synonyms}
Map the following transcript to the schema fields.
Transcript: {transcript_chunk}
Return JSON only: [{"section": "...", "field": "...", "value": "...", "confidence": 0.0-1.0}]`;

const DEFAULT_LAB_REPORT_PROMPT = `You are a medical data extraction AI. Extract lab values from the document snippets below.
We need values for the following observations. Use observation_key (or any listed alias) to identify each test:

{lab_schema}

DOCUMENT SNIPPETS:
{snippets}

Return a JSON object with single key "observations" containing an array of objects. Each object:
- observation_key: one of the keys from the schema above (or an alias that matches)
- value_numeric: number or null (MUST be the patient's result value, never the reference range)
- value_text: string for non-numeric results or null
- unit: unit of measurement (e.g. mg/dL, %, mmol/L) or null
- confidence: 0.0 to 1.0 (only include if confidence >= 0.7)
- source_text: the snippet text used for this extraction

RULES (same as consultation lab pipeline):
1. value_numeric must ALWAYS be the ACTUAL result value reported for the test, never the reference range.
2. HbA1c: extract ONLY the result in PERCENT (%). Unit must be "%". If report shows mmol/mol, convert: (mmol/mol * 0.0915 + 2.15). Do NOT use mg/dL for HbA1c (mg/dL is for glucose).
3. Hemoglobin (CBC): use name as in report (e.g. "Hemoglobin (g/dL)" or "Hemoglobin (g/L)"); put numeric result in value_numeric and unit in unit.
4. Glucose: use mg/dL. Extract FBS/Fasting and PPBS/Post-prandial when present.
5. If a snippet has no valid observation for the schema above, skip it.

Return ONLY valid JSON: {"observations": [...]}`;

const DEFAULT_LIVE_SUMMARY_PROMPT = `Generate a concise summary of the ACTIVE patient-doctor conversation.

CONVERSATION TRANSCRIPT:
{conversation_text}

EXTRACTED STRUCTURED DATA (includes form fields, lab report section, and vitals when available):
{extracted_data}

TASK: Generate a brief summary (150-200 words) of the current visit that highlights:
1. Chief complaint and presenting symptoms
2. Lab report values and vitals when present in the data above (HbA1c, glucose, CBC, weight, BMI, BP)
3. New information or changes from baseline
4. Patient concerns or questions
5. Medication adherence or side effects mentioned
6. Any critical findings or red flags

Include lab results and vital signs from the EXTRACTED STRUCTURED DATA when they are present. Focus on what's NEW and RELEVANT to this visit. Use bullet points for clarity.`;

interface PromptsEditorProps {
  configs: Dhis2ConfigOut[];
  selectedConfigId: string | null;
  onSelectConfigId: (id: string | null) => void;
}

export default function PromptsEditor({ configs, selectedConfigId, onSelectConfigId }: PromptsEditorProps) {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [labPromptText, setLabPromptText] = useState('');
  const [labPromptSaving, setLabPromptSaving] = useState(false);
  const [labPromptError, setLabPromptError] = useState<string | null>(null);

  const [liveSummaryPromptText, setLiveSummaryPromptText] = useState('');
  const selectedConfig = configs.find((c) => c.id === selectedConfigId) ?? null;
  const isWebAdapterConfig = (selectedConfig?.schema_source ?? '').toLowerCase() === 'admin_defined';

  const [liveSummarySaving, setLiveSummarySaving] = useState(false);
  const [liveSummaryError, setLiveSummaryError] = useState<string | null>(null);

  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiInstructions, setAiInstructions] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiSuccessMessage, setAiSuccessMessage] = useState<string | null>(null);

  const loadPrompts = async () => {
    if (!selectedConfigId) {
      setText('');
      setLabPromptText('');
      setLiveSummaryPromptText('');
      return;
    }
    setLoading(true);
    setError(null);
    setLabPromptError(null);
    setLiveSummaryError(null);
    try {
      const res = await dhis2ConfigAPI.getPrompts(selectedConfigId);
      setText(res.data?.extraction_prompt_text ?? '');
      setLabPromptText(res.data?.lab_report_extraction_prompt_text ?? '');
      setLiveSummaryPromptText(res.data?.dhis2_live_summary_prompt_text ?? '');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load prompts');
      setText('');
      setLabPromptText('');
      setLiveSummaryPromptText('');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPrompts();
  }, [selectedConfigId]);

  const handleSave = async () => {
    if (!selectedConfigId) return;
    setSaving(true);
    setError(null);
    try {
      await dhis2ConfigAPI.putPrompts(selectedConfigId, { extraction_prompt_text: text });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setText(DEFAULT_PROMPT);
  };

  const handleLabPromptSave = async () => {
    if (!selectedConfigId) return;
    setLabPromptSaving(true);
    setLabPromptError(null);
    try {
      await dhis2ConfigAPI.putPrompts(selectedConfigId, { lab_report_extraction_prompt_text: labPromptText });
    } catch (e: unknown) {
      setLabPromptError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setLabPromptSaving(false);
    }
  };

  const handleLabPromptReset = () => {
    setLabPromptText(DEFAULT_LAB_REPORT_PROMPT);
  };

  const handleLiveSummarySave = async () => {
    if (!selectedConfigId) return;
    setLiveSummarySaving(true);
    setLiveSummaryError(null);
    try {
      await dhis2ConfigAPI.putPrompts(selectedConfigId, { dhis2_live_summary_prompt_text: liveSummaryPromptText });
    } catch (e: unknown) {
      setLiveSummaryError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setLiveSummarySaving(false);
    }
  };

  const handleLiveSummaryReset = () => {
    setLiveSummaryPromptText(DEFAULT_LIVE_SUMMARY_PROMPT);
  };

  const handleOpenAiDialog = () => {
    setAiInstructions('');
    setAiError(null);
    setAiDialogOpen(true);
  };

  const handleCloseAiDialog = () => {
    if (aiGenerating) return;
    setAiDialogOpen(false);
    setAiError(null);
  };

  const handleGenerateAiPrompt = async () => {
    if (!selectedConfigId) return;
    const instructions = aiInstructions.trim();
    if (instructions.length < 3) {
      setAiError('Please describe your changes (at least 3 characters).');
      return;
    }
    setAiGenerating(true);
    setAiError(null);
    try {
      const res = await dhis2ConfigAPI.generateExtractionPrompt(selectedConfigId, {
        current_prompt: text,
        change_instructions: instructions,
      });
      const revised = res.data?.extraction_prompt_text ?? '';
      if (!revised) {
        setAiError('AI returned an empty prompt. Try again with clearer instructions.');
        return;
      }
      setText(revised);
      setAiDialogOpen(false);
      setAiInstructions('');
      setAiSuccessMessage('Prompt updated in the editor. Review and click Save to persist.');
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: unknown } }; message?: string };
      const detail = err.response?.data?.detail;
      let message = err.message ?? 'AI prompt generation failed';
      if (typeof detail === 'string') {
        message = detail;
      } else if (Array.isArray(detail)) {
        message = detail
          .map((d) => (typeof d === 'object' && d && 'msg' in d ? String((d as { msg?: string }).msg) : JSON.stringify(d)))
          .join('; ');
      }
      setAiError(message);
    } finally {
      setAiGenerating(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Config</InputLabel>
          <Select
            value={selectedConfigId ?? ''}
            label="Config"
            onChange={(e) => onSelectConfigId(e.target.value || null)}
          >
            <MenuItem value="">— Select config —</MenuItem>
            {configs.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="outlined" startIcon={<Restore />} onClick={handleReset} size="small">
          Reset to default
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AutoAwesome />}
          onClick={handleOpenAiDialog}
          disabled={!selectedConfigId || loading || aiGenerating}
          sx={{
            borderColor: 'primary.main',
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.light}22 0%, ${theme.palette.secondary.light}33 100%)`,
            fontWeight: 600,
            boxShadow: 1,
            transition: 'transform 0.15s ease, box-shadow 0.15s ease',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: 3,
              background: (theme) =>
                `linear-gradient(135deg, ${theme.palette.primary.light}44 0%, ${theme.palette.secondary.light}55 100%)`,
            },
          }}
        >
          AI generated prompt
        </Button>
        <Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={!selectedConfigId || saving} size="small">
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </Box>

      <Dialog open={aiDialogOpen} onClose={handleCloseAiDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesome color="primary" />
          AI generated prompt
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Do you want to make changes in the prompt? Please mention those changes / add ons below
          </Typography>
          {aiError && (
            <Alert severity="error" onClose={() => setAiError(null)} sx={{ mb: 2 }}>
              {aiError}
            </Alert>
          )}
          <TextField
            fullWidth
            multiline
            minRows={4}
            maxRows={10}
            value={aiInstructions}
            onChange={(e) => setAiInstructions(e.target.value)}
            placeholder="e.g. Add a rule to always extract allergy mentions with confidence >= 0.8"
            disabled={aiGenerating}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseAiDialog} disabled={aiGenerating}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleGenerateAiPrompt}
            disabled={aiGenerating || aiInstructions.trim().length < 3}
            startIcon={aiGenerating ? <CircularProgress size={18} color="inherit" /> : <AutoAwesome />}
          >
            {aiGenerating ? 'Generating…' : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog>

      {aiSuccessMessage && (
        <Alert severity="info" onClose={() => setAiSuccessMessage(null)} sx={{ mb: 2 }}>
          {aiSuccessMessage}
        </Alert>
      )}

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!selectedConfigId ? (
        <Typography color="text.secondary">Select a config to edit its prompts (extraction, lab report, live summary). All three are stored per config.</Typography>
      ) : loading ? (
        <Typography>Loading…</Typography>
      ) : (
        <>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Extraction prompt — placeholders: {'{schema_definition}'}, {'{synonyms}'}, {'{transcript_chunk}'}
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={12}
              maxRows={24}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={DEFAULT_PROMPT}
              sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
            />
          </Paper>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ mb: 1 }}>Lab report data extraction</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Used when DHIS2 uploads lab reports (POST /api/dhis2/lab-reports/upload) for this config. Placeholders: {'{lab_schema}'}, {'{snippets}'}.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Button variant="outlined" startIcon={<Restore />} onClick={handleLabPromptReset} size="small">
              Reset to default
            </Button>
            <Button variant="contained" startIcon={<Save />} onClick={handleLabPromptSave} disabled={labPromptSaving} size="small">
              {labPromptSaving ? 'Saving…' : 'Save lab report prompt'}
            </Button>
          </Box>
          {labPromptError && (
            <Alert severity="error" onClose={() => setLabPromptError(null)} sx={{ mb: 2 }}>
              {labPromptError}
            </Alert>
          )}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <TextField
              fullWidth
              multiline
              minRows={12}
              maxRows={24}
              value={labPromptText}
              onChange={(e) => setLabPromptText(e.target.value)}
              placeholder={DEFAULT_LAB_REPORT_PROMPT}
              sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
            />
          </Paper>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ mb: 1 }}>Live summary prompt</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {isWebAdapterConfig
              ? 'Used by web adapter live summary for sessions using this config.'
              : 'Used by adapter live summary for sessions using this config.'}
            {' '}Placeholders: {'{conversation_text}'}, {'{extracted_data}'}.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Button variant="outlined" startIcon={<Restore />} onClick={handleLiveSummaryReset} size="small">
              Reset to default
            </Button>
            <Button variant="contained" startIcon={<Save />} onClick={handleLiveSummarySave} disabled={liveSummarySaving} size="small">
              {liveSummarySaving ? 'Saving…' : 'Save live summary prompt'}
            </Button>
          </Box>
          {liveSummaryError && (
            <Alert severity="error" onClose={() => setLiveSummaryError(null)} sx={{ mb: 2 }}>
              {liveSummaryError}
            </Alert>
          )}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <TextField
              fullWidth
              multiline
              minRows={10}
              maxRows={20}
              value={liveSummaryPromptText}
              onChange={(e) => setLiveSummaryPromptText(e.target.value)}
              placeholder={DEFAULT_LIVE_SUMMARY_PROMPT}
              sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
            />
          </Paper>
        </>
      )}

      <Divider sx={{ my: 4 }} />

      <Typography variant="h6" sx={{ mb: 0.5 }}>System AI Prompts</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Global LLM prompts used across all features — chatbot, extraction, triage, prescriptions, and more.
        Stored in <code>system_config</code> as <code>prompts.*</code> keys. Edits take effect immediately — no restart needed.
      </Typography>
      <AIPromptsEditor />
    </Box>
  );
}
